import { useState } from "react";
import { cn } from "@/lib/utils";
import type { ReferenceSnapshot, ReferenceVersionList } from "@/types";
import { ReviewBadge } from "./ReferenceBadges";
import { kstShort } from "./referenceCases";
import { REVIEW_LABEL, lineDiff, reviewStatus } from "./referenceReview";

/** The version a viewer opens on: the one asked for, else the newest. */
export const pickVersion = (versions: ReferenceVersionList, id: string | null): ReferenceSnapshot | null =>
  versions.items.find((s) => s.snapshot_id === id) ?? versions.items[0] ?? null;

/**
 * Every version of one TC: pick one, read its answer (or what changed since
 * the version before it), how it was made and who reviewed it. Shared by the
 * 생성 tab's TC panel and the 검토 tab.
 */
export function ReferenceVersionViewer({
  versions,
  selectedId,
  onSelect,
  bodyClassName = "max-h-80",
}: {
  versions: ReferenceVersionList;
  selectedId: string | null;
  onSelect: (snapshotId: string) => void;
  bodyClassName?: string;
}) {
  const [compare, setCompare] = useState(false);
  const selected = pickVersion(versions, selectedId);
  if (!selected) return <p className="rounded-md bg-muted px-2.5 py-2 text-muted-foreground">아직 만든 Reference가 없습니다.</p>;
  const previous = versions.items.find((s) => s.version < selected.version) ?? null;
  const diff = compare && previous ? lineDiff(previous.answer_body, selected.answer_body) : null;
  const origin = selected.origin;

  return (
    <div className="grid gap-2.5">
      <ul className="flex flex-wrap gap-1.5" aria-label="버전">
        {versions.items.map((s) => {
          const status = reviewStatus(s);
          return (
            <li key={s.snapshot_id}>
              <button
                type="button"
                aria-pressed={s.snapshot_id === selected.snapshot_id}
                onClick={() => onSelect(s.snapshot_id)}
                className={cn(
                  "grid min-w-24 gap-px rounded-md border border-border bg-card px-2 py-1 text-left text-[11.5px]",
                  s.snapshot_id === selected.snapshot_id && "border-primary ring-1 ring-primary",
                )}
              >
                <b className="text-xs">
                  v{s.version}
                  {s.active && " · 현재 기준"}
                </b>
                <span className={cn("text-[10.5px]", status === "passed" ? "text-success" : "text-accent-foreground")}>
                  {REVIEW_LABEL[status]}
                </span>
                <span className="text-muted-foreground tabular-nums">{kstShort(s.created_at)}</span>
              </button>
            </li>
          );
        })}
      </ul>

      <div className="grid gap-1">
        <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-muted-foreground">
          <span>
            v{selected.version} 답변 · {selected.model_name}
          </span>
          {previous && (
            <label className="inline-flex cursor-pointer items-center gap-1">
              <input type="checkbox" className="size-3.5 accent-primary" checked={compare} onChange={(e) => setCompare(e.target.checked)} />
              v{previous.version}와 비교
            </label>
          )}
        </div>
        {compare && previous && diff ? (
          <div className={cn("overflow-y-auto rounded-md bg-muted py-1.5 text-xs leading-relaxed", bodyClassName)} aria-label={`v${previous.version}에서 달라진 줄`}>
            {diff.map((line, i) => (
              <div
                key={i}
                className={cn(
                  "px-2.5 whitespace-pre-wrap",
                  line.kind === "add" && "bg-success-surface",
                  line.kind === "del" && "bg-destructive-surface line-through decoration-destructive/50",
                )}
              >
                <span className="sr-only">{line.kind === "add" ? "추가: " : line.kind === "del" ? "삭제: " : ""}</span>
                {line.text || " "}
              </div>
            ))}
          </div>
        ) : (
          <div className={cn("overflow-y-auto rounded-md bg-muted px-2.5 py-2 text-xs leading-relaxed whitespace-pre-wrap", bodyClassName)}>
            {compare && previous && !diff && <p className="mb-1 text-muted-foreground">답변이 길어 비교하지 않고 본문만 보여 줍니다.</p>}
            {selected.answer_body}
          </div>
        )}
      </div>

      <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-0.5 text-[11.5px]">
        <dt className="text-muted-foreground">생성</dt>
        <dd className="tabular-nums">{kstShort(selected.created_at)}</dd>
        {origin ? (
          <>
            <dt className="text-muted-foreground">설정</dt>
            <dd className="break-words">
              {origin.model} · 추론 {origin.reasoning_effort ?? "기본"} · 대화 {origin.history_limit ?? "기본"}개
              {origin.git_ref && ` · ${origin.git_ref.slice(0, 7)}`}
            </dd>
            <dt className="text-muted-foreground">실행</dt>
            <dd className="font-mono text-[11px] break-all">
              {origin.qa_replay_run_id ? origin.qa_replay_run_id.slice(0, 8) : "—"}
              {origin.llm_turn_count != null && ` · LLM turn ${origin.llm_turn_count}`}
            </dd>
            {origin.requested_by_email && (
              <>
                <dt className="text-muted-foreground">요청</dt>
                <dd className="break-all">{origin.requested_by_email}</dd>
              </>
            )}
          </>
        ) : (
          <>
            <dt className="text-muted-foreground">설정</dt>
            <dd className="text-muted-foreground">기록 없음 (검토 기능 이전에 만든 버전)</dd>
          </>
        )}
      </dl>

      {(selected.reviews?.length ?? 0) > 0 && (
        <ol className="grid gap-1.5" aria-label={`v${selected.version} 검토 기록`}>
          {selected.reviews!.map((r) => (
            <li key={r.review_id} className="grid gap-0.5 rounded-md border border-border px-2.5 py-1.5 text-[11.5px]">
              <div className="flex flex-wrap items-center gap-1.5">
                {r.decision === "passed" ? (
                  <ReviewBadge status="passed" />
                ) : (
                  <span className="rounded-full bg-muted px-1.5 text-[10.5px]">관리자 피드백</span>
                )}
                <span className="ml-auto text-muted-foreground tabular-nums">
                  {r.reviewer_email} · {kstShort(r.reviewed_at)}
                </span>
              </div>
              {r.comment && <p className="whitespace-pre-wrap">{r.comment}</p>}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
